import { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';

import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

import { useAuth } from 'contexts/AuthContext';
import { applyPostLogoutRedirect } from 'utils/postLogoutRedirect';

function stringAvatar(name = '') {
  const parts = name.trim().split(' ');
  const initials = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : (parts[0]?.[0] ?? '?');
  return initials.toUpperCase();
}

export default function Profile() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    applyPostLogoutRedirect(navigate);
  };

  const displayName = user?.name || user?.email || '';
  const roleLabel = user?.role ? t(`roles.${user.role}`, user.role) : '';
  const avatarUrl = user?.avatarUrl ?? undefined;

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <Tooltip title={displayName} disableInteractive>
        <ButtonBase
          sx={(theme) => ({
            p: 0.25,
            borderRadius: 1,
            '&:focus-visible': { outline: `2px solid ${theme.vars.palette.secondary.dark}`, outlineOffset: 2 }
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Avatar
            src={avatarUrl}
            sx={{
              width: 32,
              height: 32,
              fontSize: 13,
              fontWeight: 600,
              bgcolor: 'primary.main',
              '&:hover': { outline: '1px solid', outlineColor: 'primary.dark' }
            }}
          >
            {!avatarUrl && stringAvatar(displayName)}
          </Avatar>
        </ButtonBase>
      </Tooltip>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 240 })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <Box sx={{ px: 2.5, py: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={avatarUrl} sx={{ width: 36, height: 36, fontSize: 14, fontWeight: 600, bgcolor: 'primary.main' }}>
                        {!avatarUrl && stringAvatar(displayName)}
                      </Avatar>
                      <Stack>
                        <Typography variant="subtitle1" noWrap sx={{ maxWidth: 160 }}>
                          {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                          {roleLabel}
                        </Typography>
                        {user?.role !== 'superadmin' && user?.creditAvailable != null && (
                          <Typography variant="caption" color="primary.main" noWrap sx={{ maxWidth: 160 }}>
                            {t('credits.available', { count: user.creditAvailable })}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Box>

                  <Divider />

                  <List component="nav" sx={{ p: 0.5 }}>
                    <ListItemButton component={Link} to="/profile" onClick={() => setOpen(false)} sx={{ borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <UserOutlined />
                      </ListItemIcon>
                      <ListItemText primary={t('profile.myProfile', 'My Profile')} />
                    </ListItemButton>
                    <ListItemButton onClick={handleLogout} sx={{ borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LogoutOutlined />
                      </ListItemIcon>
                      <ListItemText primary={t('profile.logout', 'Logout')} />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
